import './techinfocard.css';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import Spiner from '../Spiner/Spiner';
import Table, {
  LabeledCell,
  LabeledSecondaryCell,
  ColumnConfig,
  ExpandedRowConfig,
  SecondaryRowConfig,
} from '../Table/Table';
import StatusCell from '../Table/StatusCell';
import { normalizeImageUrl, fullName } from '../../utils/utils';
import { formatBackendShortDate, formatBackendTime } from '../../utils/date.utils';
import { ICar, ITechCheckItem } from '../../types/type';
import PhotoItem from '../PhotoItem/PhotoItem';
import DropDownInput from '../DropDownInput/DropDownInput';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSelectedTechData, updateUserTechData } from '../../store/techdata/techdataSlice';
import { CHECK_STATUS_FINAL_MESSAGE, isCheckStatusFinal } from '../../utils/checkStatusErrors';
import { TechnicalService, DiagnosticCardVerification } from '../../services/technical.service';
import { useState, useEffect } from 'react';
import Modal from '../Modal/Modal';
import { CAR_FORBIDDEN_MESSAGE, CarService, isCarForbidden } from '../../services/car.service';
import TechHistory from './TechHistory';

function TechInfoCard({ selectedTechList }: { selectedTechList: ITechCheckItem }) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const user = useAppSelector((state: RootState) => state.user.user);
  const [odometerValue, setOdometerValue] = useState<number>(selectedTechList?.odometerReading || 0);
  // Галочка стоит, только если автосогласование назначено на этого техника (как в epl_web)
  const ownsAutoApproval = (car?: ICar) => Boolean(car?.autonegotiation && car.autonegotiationTechnikID === user?.id);
  const [allowAutoApproval, setAllowAutoApproval] = useState<boolean>(ownsAutoApproval(selectedTechList?.car));
  const [diagnosticCardVerified, setDiagnosticCardVerified] = useState<boolean>(
    selectedTechList?.car?.diagnosticCardVerified || false,
  );
  const [showReasonModal, setShowReasonModal] = useState<boolean>(false);
  const [reasonForRefusal, setReasonForRefusal] = useState<string>('');

  // Инициализация значений из selectedTechList при загрузке или изменении
  useEffect(() => {
    if (selectedTechList) {
      setOdometerValue(selectedTechList.odometerReading || 0);
      setDiagnosticCardVerified(selectedTechList.car?.diagnosticCardVerified || false);
      setAllowAutoApproval(ownsAutoApproval(selectedTechList.car));
    }
  }, [selectedTechList, user?.id]);

  if (!selectedTechList) {
    return <Spiner />;
  }

  // Статус-маппинг для визуализации статусов
  const statusMap = {
    completed: { label: 'Допущен', dotClass: 'active' },
    rejected: { label: 'Не допущен', dotClass: 'rejected' },
    waiting: { label: 'Ждет контроля', dotClass: 'waiting' },
    inProgress: { label: 'В процессе', dotClass: 'inProgress' },
  };

  // Опции для выпадающего списка статусов
  const dropdownStatus = [
    { text: 'Допущен', value: 'completed' },
    { text: 'Не допущен', value: 'rejected' },
    { text: 'Ждет контроля', value: 'waiting' },
  ];

  // Проверка условий для положительного решения
  const checkPositiveConditions = () => {
    return (
      selectedTechList.engine &&
      selectedTechList.optic &&
      selectedTechList.body &&
      selectedTechList.chassis &&
      diagnosticCardVerified
    );
  };

  // Обработчик изменения одометра
  const handleOdometerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOdometerValue(+e.target.value);
  };

  // Обработчик изменения статуса автосогласования
  const handleAutoApprovalChange = async (status: boolean) => {
    if (selectedTechList && selectedTechList.car && selectedTechList.car.id) {
      try {
        setLoading(true);
        if (status) {
          await CarService.updateCarTechnik(selectedTechList.car.id, user.id);
          await TechnicalService.updateCarAutonegotiation(selectedTechList.car.id, user.id, status);
        } else {
          await CarService.updateCarTechnik(selectedTechList.car.id, null);
        }
        setAllowAutoApproval(status);
      } catch (error) {
        if (isCarForbidden(error)) {
          // Автосогласование уже у другого техника — у этого техника его нет
          setAllowAutoApproval(false);
          alert(CAR_FORBIDDEN_MESSAGE);
          return;
        }
        console.error('Ошибка при обновлении статуса автосогласования:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Обработчик сохранения изменений одометра
  const saveOdometerChanges = async () => {
    if (selectedTechList && selectedTechList.id) {
      try {
        setLoading(true);
        await dispatch(updateUserTechData({ id: selectedTechList.id, updates: { odometerReading: +odometerValue } }));
        // await TechnicalService.updateOdometerReading(selectedTechList.id, odometerValue, selectedTechList.technicID);
      } catch (error) {
        console.error('Ошибка при обновлении значения одометра:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Обработчик изменения статуса диагностической карты
  const handleDiagnosticCardChange = async (verified: boolean) => {
    if (selectedTechList && selectedTechList.car && selectedTechList.car.id) {
      try {
        setLoading(true);
        const verificationData: DiagnosticCardVerification = { verified };
        await TechnicalService.updateDiagnosticCardVerification(selectedTechList.car.id, verificationData);
        setDiagnosticCardVerified(verified);
      } catch (error) {
        console.error('Ошибка при обновлении статуса диагностической карты:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Обработчик изменения статуса
  const handleStatusChange = async (newStatus: 'waiting' | 'completed' | 'rejected') => {
    if (selectedTechList && selectedTechList.id) {
      // Проверяем условия для положительного решения
      if (newStatus === 'completed' && !checkPositiveConditions()) {
        alert('Невозможно принять положительное решение! Не все условия выполнены.');
        return;
      }

      // Показываем модальное окно для обоснования при отрицательном решении
      if (newStatus === 'rejected') {
        setShowReasonModal(true);
        return;
      }

      try {
        setLoading(true);
        await TechnicalService.updateTechListStatus(selectedTechList.id, newStatus, user.id);

        // Обновляем данные в Redux-хранилище
        dispatch(fetchSelectedTechData({ id: selectedTechList.id }));
      } catch (error) {
        if (isCheckStatusFinal(error)) {
          alert(CHECK_STATUS_FINAL_MESSAGE);
          dispatch(fetchSelectedTechData({ id: selectedTechList.id }));
        } else {
          console.error('Ошибка при обновлении статуса:', error);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Обработчик отправки обоснования отказа
  const handleReasonSubmit = async () => {
    if (selectedTechList && selectedTechList.id && reasonForRefusal) {
      try {
        setLoading(true);
        await TechnicalService.updateTechListStatus(selectedTechList.id, 'rejected', user?.id, reasonForRefusal);

        // Обновляем данные в Redux-хранилище
        dispatch(fetchSelectedTechData({ id: selectedTechList.id }));
        setShowReasonModal(false);
      } catch (error) {
        if (isCheckStatusFinal(error)) {
          alert(CHECK_STATUS_FINAL_MESSAGE);
          dispatch(fetchSelectedTechData({ id: selectedTechList.id }));
          setShowReasonModal(false);
        } else {
          console.error('Ошибка при обновлении статуса с причиной отказа:', error);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Конфигурация колонок для таблицы
  const tableColumns: ColumnConfig[] = [
    {
      id: 'date',
      title: 'Дата',
      render: (row: ITechCheckItem) => (
        <>
          <div className="table-cell-title">Дата</div>
          <div className="table-cell-value">{formatBackendShortDate(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'time',
      title: 'Время',
      render: (row: ITechCheckItem) => (
        <>
          <div className="table-cell-title">Время</div>
          <div className="table-cell-value">{formatBackendTime(row.checkTime)}</div>
        </>
      ),
    },
    {
      id: 'autopark',
      title: 'Автопарк',
      render: (row: ITechCheckItem) => (
        <>
          <div className="table-cell-title">Автопарк</div>
          <div className="table-cell-value">{row?.autopark?.legalName || 'Не указан'}</div>
        </>
      ),
    },

    {
      id: 'num',
      title: 'Номер заявки',
      render: (row: ITechCheckItem) => (
        <>
          <div className="table-cell-title">Номер заявки</div>
          <div className="table-cell-value">{row.num}</div>
        </>
      ),
    },
    {
      id: 'status',
      title: 'Статус',
      render: (row: ITechCheckItem) => {
        const status = row.checkStatus || 'waiting';
        return <StatusCell status={status} statusMap={statusMap} />;
      },
      className: 'table-cell-centered',
    },
    {
      id: 'actions',
      title: 'Смотреть',
      render: () => null,
    },
  ];

  // Конфигурация вторичных строк (всегда видимых)
  const secondaryRows: SecondaryRowConfig[] = [
    {
      id: 'all-info',
      condition: (row: ITechCheckItem) => true,
      content: (row: ITechCheckItem) => (
        <>
          <LabeledSecondaryCell title="ФИО" value={fullName(row?.profile) || 'Не указан'} colSpan={2} />

          <LabeledSecondaryCell title="Номер авто" value={row.car.number || 'Не указан'} colSpan={1} />
          <LabeledSecondaryCell
            title="Марка/модель авто"
            value={row.car.mark && row.car.model ? `${row.car.mark} ${row.car.model}` : 'Не указан'}
            colSpan={1}
          />
        </>
      ),
    },
  ];

  // Конфигурация расширяемых строк
  const expandedRows: ExpandedRowConfig[] = [
    {
      id: 'parameters',
      content: (row: ITechCheckItem) => (
        <>
          <LabeledCell title="Двигатель" value={row.engine ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Оптика" value={row.optic ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Кузов и салон" value={row.body ? 'Исправно' : 'Не исправно'} />
          <LabeledCell title="Ходовая" value={row.chassis ? 'Исправно' : 'Не исправно'} />
          {row.checkStatus !== 'completed' && row.checkStatus !== 'rejected' ? (
            <LabeledCell
              title="Одометр"
              colSpan={2}
              value={
                <div className="parameter-row parameter-odometer-row">
                  <input
                    type="text"
                    value={odometerValue}
                    onChange={handleOdometerChange}
                    className="parameter-input"
                  />
                  <button onClick={saveOdometerChanges} className="parameter-button">
                    Сохранить
                  </button>
                </div>
              }
            />
          ) : (
            <LabeledCell title="Одометр" value={row.odometerReading || '-'} />
          )}
        </>
      ),
    },
    {
      id: 'extraParameters',
      content: (row: ITechCheckItem) => (
        <>
          <LabeledCell
            title="Разрешить автосогласование"
            value={
              <div className="parameter-row">
                <input
                  type="checkbox"
                  checked={allowAutoApproval}
                  onChange={(e) => handleAutoApprovalChange(e.target.checked)}
                  disabled={row.checkStatus === 'completed' || row.checkStatus === 'rejected'}
                  className="parameter-checkbox"
                />
              </div>
            }
          />

          <LabeledCell
            title="Диагностическая карта проверена"
            value={
              <div className="parameter-row">
                <input
                  type="checkbox"
                  checked={diagnosticCardVerified}
                  onChange={(e) => handleDiagnosticCardChange(e.target.checked)}
                  disabled={row.checkStatus === 'completed' || row.checkStatus === 'rejected'}
                  className="parameter-checkbox"
                />
              </div>
            }
          />

          {row.checkStatus !== 'completed' && row.checkStatus !== 'rejected' && (
            <div className="conclusion-row">
              <DropDownInput
                label="Заключение"
                items={dropdownStatus}
                onSelect={handleStatusChange}
                initialValue={row.checkStatus}
                editable={true}
              />
            </div>
          )}
        </>
      ),
    },
    {
      id: 'photos',
      content: (row: ITechCheckItem) => (
        <>
          <div
            className="myphoto__box"
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              gap: '10px',
              width: '100%',
            }}
          >
            {row.car?.carImages?.length > 0 ? (
              <>
                <PhotoItem
                  photoType="front"
                  title="Фронтальная"
                  img={row.car.carImages.find((img) => img.photoType === 'front')?.photoUrl || null}
                  subClass={'bigger'}
                  carId={row.car.id}
                />
                <PhotoItem
                  photoType="back"
                  title="Задняя"
                  img={row.car.carImages.find((img) => img.photoType === 'back')?.photoUrl || null}
                  subClass={'bigger'}
                  carId={row.car.id}
                />
                <PhotoItem
                  photoType="left"
                  title="Левая"
                  img={row.car.carImages.find((img) => img.photoType === 'left')?.photoUrl || null}
                  subClass={'bigger'}
                  carId={row.car.id}
                />
                <PhotoItem
                  photoType="right"
                  title="Правая"
                  img={row.car.carImages.find((img) => img.photoType === 'right')?.photoUrl || null}
                  subClass={'bigger'}
                  carId={row.car.id}
                />
                <PhotoItem
                  photoType="trunk"
                  title="Багажник"
                  img={row.car.carImages.find((img) => img.photoType === 'trunk')?.photoUrl || null}
                  subClass={'bigger'}
                  carId={row.car.id}
                />
              </>
            ) : (
              <div>Фотографии отсутствуют</div>
            )}
          </div>
          {row.car?.diagnosticCardPhoto && (
            <div
              style={{
                marginTop: '12px',
                display: 'inline-flex',
                flexDirection: 'column',
                gap: '10px',
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '10px',
                background: '#fafafa',
                minWidth: '220px',
                maxWidth: '320px',
              }}
            >
              <div style={{ fontWeight: 600 }}>Диагностическая карта</div>
              <img
                src={normalizeImageUrl(row.car.diagnosticCardPhoto)}
                alt="Фото диагностической карты"
                style={{ width: '100%', borderRadius: '8px', objectFit: 'cover' }}
              />
              <a
                href={normalizeImageUrl(row.car.diagnosticCardPhoto)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1E88E5', textDecoration: 'none', fontWeight: 500 }}
              >
                Открыть в полном размере
              </a>
            </div>
          )}
        </>
      ),
    },
    {
      id: 'odometer',
      content: (row: ITechCheckItem) =>
        row.odometerPhoto && (
          <div className="myphoto__box">
            <PhotoItem
              photoType="odometer"
              title="Одометр"
              img={row.odometerPhoto || null}
              subClass={'bigger'}
              carId={row.car.id}
            />
          </div>
        ),
    },
  ];

  // Подготовка данных для таблицы
  const tableData = selectedTechList ? [selectedTechList] : [];

  return (
    <div className="techinfo">
      <div className="infoCard">
        <div className="infoCardHeader">
          <div className="infoCardHeaderTitle">Информация</div>
        </div>
        <div className="techinfoContainer">
          <Table
            data={tableData}
            columns={tableColumns}
            expandedRows={expandedRows}
            secondaryRows={secondaryRows}
            keyField="id"
            noDataMessage="Нет данных для отображения"
          />
        </div>
      </div>

      {showReasonModal && (
        <Modal title="Обоснование отрицательного заключения" onClose={() => setShowReasonModal(false)}>
          <div className="modal-content">
            <div className="modal-form-group">
              <div className="modal-label">Укажите причину отказа:</div>
              <textarea
                rows={5}
                className="modal-textarea"
                value={reasonForRefusal}
                onChange={(e) => setReasonForRefusal(e.target.value)}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowReasonModal(false)} className="modal-button modal-button-cancel">
                Отмена
              </button>
              <button
                onClick={handleReasonSubmit}
                className="modal-button modal-button-confirm"
                disabled={!reasonForRefusal.trim()}
              >
                Подтвердить
              </button>
            </div>
          </div>
        </Modal>
      )}
      {selectedTechList?.technicID && (
        <>
          {/* Компонент истории технических проверок */}
          <TechHistory
            selectedTechList={selectedTechList}
            tableColumns={tableColumns}
            expandedRows={expandedRows}
            secondaryRows={secondaryRows}
            statusMap={statusMap}
          />
        </>
      )}
    </div>
  );
}

export default TechInfoCard;
